import { useCallback, useMemo, useState } from "react"

import {
  DEFAULT_SCHEDULE_STATE,
  DEFAULT_SELECTED_DAYS,
  RECURRENCE_TYPE,
  SCHEDULE_TYPE,
  TIME_UNIT,
  type RecurrenceType,
  type ScheduleAmPm,
  type ScheduleType,
  type TimeUnit,
} from "@/lib/schedule"

import {
  buildScheduleExpressionFromState,
  deriveScheduleFormState,
} from "../utils/scheduleStateUtils"

const MIN_INTERVAL_MINUTES = 15
const MAX_INTERVAL_MINUTES = 43_200
const MIN_HOUR = 1
const MAX_HOUR = 12
const MIN_MINUTE = 0
const MAX_MINUTE = 59

export interface ScheduleFormState {
  scheduleType: ScheduleType
  selectedPreset: number | null
  customValue: string
  customUnit: TimeUnit
  scheduleHour: string
  scheduleMinute: string
  scheduleAmPm: ScheduleAmPm
  recurrenceType: RecurrenceType
  selectedDays: Set<number>
}

export interface ScheduleFormActions {
  setScheduleType: (value: ScheduleType) => void
  setSelectedPreset: (value: number | null) => void
  setCustomValue: (value: string) => void
  setCustomUnit: (value: TimeUnit) => void
  setScheduleHour: (value: string) => void
  setScheduleMinute: (value: string) => void
  setScheduleAmPm: (value: ScheduleAmPm) => void
  setRecurrenceType: (value: RecurrenceType) => void
  setSelectedDays: (value: Set<number>) => void
  reset: (scheduleExpression?: string) => void
}

export interface ScheduleFormValidation {
  isCustomValid: boolean
  isFormValid: boolean
  isTimeOfDayValid: boolean
}

function getDefaultScheduleState(): ScheduleFormState {
  return {
    scheduleType: DEFAULT_SCHEDULE_STATE.scheduleType,
    selectedPreset: DEFAULT_SCHEDULE_STATE.selectedPreset,
    customValue: DEFAULT_SCHEDULE_STATE.customValue,
    customUnit: DEFAULT_SCHEDULE_STATE.customUnit,
    scheduleHour: DEFAULT_SCHEDULE_STATE.scheduleHour,
    scheduleMinute: DEFAULT_SCHEDULE_STATE.scheduleMinute,
    scheduleAmPm: DEFAULT_SCHEDULE_STATE.scheduleAmPm,
    recurrenceType: DEFAULT_SCHEDULE_STATE.recurrenceType,
    selectedDays: new Set(DEFAULT_SELECTED_DAYS),
  }
}

function getInitialScheduleState(
  scheduleExpression?: string,
): ScheduleFormState {
  if (!scheduleExpression) {
    return getDefaultScheduleState()
  }

  const derived = deriveScheduleFormState(scheduleExpression)
  return {
    scheduleType: derived.scheduleType,
    selectedPreset: derived.presetIndex,
    customValue: derived.customValue,
    customUnit: derived.customUnit,
    scheduleHour: derived.hour,
    scheduleMinute: derived.minute,
    scheduleAmPm: derived.amPm,
    recurrenceType: derived.recurrenceType,
    selectedDays: new Set(derived.selectedDays),
  }
}

export function useScheduleForm(initialScheduleExpression?: string) {
  const initialState = getInitialScheduleState(initialScheduleExpression)
  const [scheduleType, setScheduleType] = useState(initialState.scheduleType)
  const [selectedPreset, setSelectedPreset] = useState(
    initialState.selectedPreset,
  )
  const [customValue, setCustomValue] = useState(initialState.customValue)
  const [customUnit, setCustomUnit] = useState(initialState.customUnit)
  const [scheduleHour, setScheduleHour] = useState(initialState.scheduleHour)
  const [scheduleMinute, setScheduleMinute] = useState(
    initialState.scheduleMinute,
  )
  const [scheduleAmPm, setScheduleAmPm] = useState(initialState.scheduleAmPm)
  const [recurrenceType, setRecurrenceType] = useState(
    initialState.recurrenceType,
  )
  const [selectedDays, setSelectedDays] = useState(initialState.selectedDays)

  const reset = useCallback((scheduleExpression?: string) => {
    const nextState = getInitialScheduleState(scheduleExpression)
    setScheduleType(nextState.scheduleType)
    setSelectedPreset(nextState.selectedPreset)
    setCustomValue(nextState.customValue)
    setCustomUnit(nextState.customUnit)
    setScheduleHour(nextState.scheduleHour)
    setScheduleMinute(nextState.scheduleMinute)
    setScheduleAmPm(nextState.scheduleAmPm)
    setRecurrenceType(nextState.recurrenceType)
    setSelectedDays(nextState.selectedDays)
  }, [])

  const state = useMemo<ScheduleFormState>(
    () => ({
      scheduleType,
      selectedPreset,
      customValue,
      customUnit,
      scheduleHour,
      scheduleMinute,
      scheduleAmPm,
      recurrenceType,
      selectedDays,
    }),
    [
      customUnit,
      customValue,
      recurrenceType,
      scheduleAmPm,
      scheduleHour,
      scheduleMinute,
      scheduleType,
      selectedDays,
      selectedPreset,
    ],
  )

  const isCustomValid = useMemo(() => {
    if (selectedPreset !== null) {
      return true
    }

    const value = Number.parseInt(customValue, 10)
    if (Number.isNaN(value) || value < 1) {
      return false
    }

    const totalMinutes =
      customUnit === TIME_UNIT.MINUTES
        ? value
        : customUnit === TIME_UNIT.HOURS
          ? value * 60
          : value * 1_440

    return (
      totalMinutes >= MIN_INTERVAL_MINUTES &&
      totalMinutes <= MAX_INTERVAL_MINUTES
    )
  }, [customUnit, customValue, selectedPreset])

  const isTimeOfDayValid = useMemo(() => {
    const hour = Number.parseInt(scheduleHour, 10)
    const minute = Number.parseInt(scheduleMinute, 10)

    if (
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      hour < MIN_HOUR ||
      hour > MAX_HOUR ||
      minute < MIN_MINUTE ||
      minute > MAX_MINUTE
    ) {
      return false
    }

    return !(
      recurrenceType === RECURRENCE_TYPE.WEEKLY && selectedDays.size === 0
    )
  }, [recurrenceType, scheduleHour, scheduleMinute, selectedDays])

  const isFormValid =
    scheduleType === SCHEDULE_TYPE.INTERVAL ? isCustomValid : isTimeOfDayValid

  const expression = useMemo(
    () => buildScheduleExpressionFromState(state),
    [state],
  )

  const actions = useMemo<ScheduleFormActions>(
    () => ({
      setScheduleType,
      setSelectedPreset,
      setCustomValue,
      setCustomUnit,
      setScheduleHour,
      setScheduleMinute,
      setScheduleAmPm,
      setRecurrenceType,
      setSelectedDays,
      reset,
    }),
    [reset],
  )

  const validation = useMemo<ScheduleFormValidation>(
    () => ({ isCustomValid, isFormValid, isTimeOfDayValid }),
    [isCustomValid, isFormValid, isTimeOfDayValid],
  )

  return { actions, expression, state, validation }
}
